/**
 * Circular- and DOM-safe JSON serialization.
 *
 * Native `JSON.stringify` throws `TypeError: Converting circular structure to
 * JSON` the moment it walks into a value that references itself. The most
 * common production trigger for this in a React app is a DOM node sneaking
 * into otherwise-serializable data: React attaches an own-enumerable
 * `__reactFiber$<id>` property to every mounted DOM node, and that fiber's
 * `stateNode` points straight back at the node — an instant cycle
 * (`HTMLInputElement -> __reactFiber$… -> FiberNode -> stateNode -> …`).
 *
 * That throw is especially nasty because it surfaces from *minified*,
 * anonymous call sites we don't own — e.g. TanStack Query's `hashKey`, which
 * `JSON.stringify`s every query key to compute its cache identity. A single
 * stray DOM reference in a query key takes down the whole render with no
 * actionable stack.
 *
 * These helpers degrade gracefully instead: DOM nodes / events / windows are
 * replaced with a stable descriptor, and any other cycle is replaced with a
 * `"[Circular]"` sentinel. The output stays deterministic, so it remains a
 * valid cache key / change-detection key.
 */

function describeDomLike(value: object): string | null {
  if (typeof Node !== "undefined" && value instanceof Node) {
    const tag =
      value instanceof Element ? value.tagName.toLowerCase() : value.nodeName;
    return `[DOMNode:${tag}]`;
  }
  if (typeof Event !== "undefined" && value instanceof Event) {
    return `[Event:${value.type}]`;
  }
  if (typeof Window !== "undefined" && value instanceof Window) {
    return "[Window]";
  }
  return null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) return false;
  const proto = Object.getPrototypeOf(value) as object | null;
  return proto === null || proto === Object.prototype;
}

/**
 * Builds a `JSON.stringify` replacer that:
 *  - substitutes DOM nodes / events / windows with a stable descriptor,
 *  - replaces circular references with `"[Circular]"`,
 *  - optionally sorts plain-object keys (so `{a,b}` and `{b,a}` serialize
 *    identically — required for stable cache keys).
 *
 * Cycle detection uses a `WeakSet` of values currently on the serialization
 * path. JSON's replacer has no "leaving node" hook, so we cannot pop reliably;
 * to avoid false positives on shared-but-acyclic references (diamonds) we only
 * ever flag a value once. For cache keys and change-detection keys this is the
 * right trade-off: determinism and not throwing matter, exact diamond fidelity
 * does not.
 */
function createSafeReplacer(sortKeys: boolean) {
  const seen = new WeakSet<object>();
  return function replacer(this: unknown, _key: string, value: unknown) {
    if (typeof value !== "object" || value === null) return value;

    const domDescriptor = describeDomLike(value);
    if (domDescriptor !== null) return domDescriptor;

    if (seen.has(value)) return "[Circular]";
    seen.add(value);

    if (sortKeys && isPlainObject(value)) {
      const sorted: Record<string, unknown> = {};
      for (const key of Object.keys(value).sort()) {
        sorted[key] = value[key];
      }
      return sorted;
    }

    return value;
  };
}

/**
 * Drop-in replacement for `JSON.stringify(value)` that never throws on
 * circular structures or DOM references. Preserves key insertion order so the
 * output matches plain `JSON.stringify` for ordinary (acyclic, DOM-free) data.
 */
export function safeStringify(value: unknown): string {
  return JSON.stringify(value, createSafeReplacer(false)) ?? "";
}

/**
 * Circular-safe query-key hasher for TanStack Query's `queryKeyHashFn`.
 *
 * Mirrors the default `hashKey` behaviour (plain-object keys are sorted so key
 * order never changes a query's cache identity) but tolerates DOM references
 * and cycles instead of throwing. Wire it into the `QueryClient`'s
 * `defaultOptions.queries.queryKeyHashFn`.
 */
export function hashQueryKey(queryKey: ReadonlyArray<unknown>): string {
  return JSON.stringify(queryKey, createSafeReplacer(true)) ?? "";
}
